import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AdminInbox.css';

const API_ROOT = '/api/admin/mail';

const formatDate = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const initials = (name, email) => {
  const source = (name || email || '?').trim();
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

const AdminInbox = () => {
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('inbox');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_ROOT}?view=${encodeURIComponent(filter)}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Inbox returned ${response.status}`);
      const payload = await response.json();
      setMessages(payload.messages || []);
      setSelectedId((current) => {
        if (current && payload.messages?.some((message) => message.id === current)) return current;
        return null;
      });
    } catch (requestError) {
      setError('The secure mailbox is not connected yet. Finish the Cloudflare Worker, D1, and Access setup to start receiving mail here.');
      setMessages([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedMessage(null);
      return;
    }

    let active = true;
    setDetailLoading(true);
    fetch(`${API_ROOT}/${encodeURIComponent(selectedId)}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Message returned ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (active) {
          setSelectedMessage(payload.message);
          setMessages((current) => current.map((message) => (
            message.id === payload.message.id ? { ...message, status: payload.message.status } : message
          )));
        }
      })
      .catch(() => {
        if (active) setError('That message could not be opened. Try refreshing the inbox.');
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedId]);

  const visibleMessages = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return messages;
    return messages.filter((message) =>
      [message.sender_name, message.sender, message.subject, message.preview, message.recipient]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }, [messages, query]);

  const updateStatus = async (id, status) => {
    setError('');
    const response = await fetch(`${API_ROOT}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      setError('The message could not be updated. Please try again.');
      return;
    }
    await loadMessages();
  };

  const unreadCount = messages.filter((message) => message.status === 'unread').length;

  return (
    <main className="mail-admin">
      <header className="mail-topbar">
        <a className="mail-brand" href="/" aria-label="Brickstone Studio home">
          <img src="/brickstone-logo.png" alt="" />
          <span>Brickstone Mail</span>
        </a>
        <div className="mail-secure-status"><span aria-hidden="true" />Protected by Cloudflare Access</div>
      </header>

      <div className="mail-shell">
        <aside className="mail-sidebar" aria-label="Mailbox navigation">
          <div>
            <p className="mail-sidebar-label">Mailboxes</p>
            <button className={filter === 'inbox' ? 'active' : ''} onClick={() => setFilter('inbox')}>
              <span>Inbox</span><strong>{unreadCount}</strong>
            </button>
            <button className={filter === 'archived' ? 'active' : ''} onClick={() => setFilter('archived')}>
              <span>Archive</span>
            </button>
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
              <span>All mail</span>
            </button>
          </div>
          <div className="mail-addresses">
            <p className="mail-sidebar-label">Receiving at</p>
            <span>info@brickstone.plus</span>
            <span>privacy@brickstone.plus</span>
          </div>
        </aside>

        <section className="mail-list-panel" aria-labelledby="inbox-heading">
          <div className="mail-list-header">
            <div>
              <p className="mail-eyebrow">Private workspace</p>
              <h1 id="inbox-heading">{filter === 'archived' ? 'Archive' : filter === 'all' ? 'All mail' : 'Inbox'}</h1>
            </div>
            <button className="mail-icon-button" onClick={loadMessages} aria-label="Refresh inbox">↻</button>
          </div>
          <label className="mail-search">
            <span className="sr-only">Search mail</span>
            <span aria-hidden="true">⌕</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search mail" />
          </label>

          {error && <div className="mail-notice" role="status"><strong>Mailbox setup needed</strong><span>{error}</span></div>}
          {loading && <div className="mail-empty">Loading your inbox...</div>}
          {!loading && !error && visibleMessages.length === 0 && (
            <div className="mail-empty"><strong>Nothing here yet</strong><span>New messages will appear as soon as Cloudflare receives them.</span></div>
          )}

          <div className="mail-message-list">
            {visibleMessages.map((message) => (
              <button
                key={message.id}
                className={`mail-message-row ${message.id === selectedId ? 'selected' : ''} ${message.status === 'unread' ? 'unread' : ''}`}
                onClick={() => setSelectedId(message.id)}
              >
                <span className="mail-avatar" aria-hidden="true">{initials(message.sender_name, message.sender)}</span>
                <span className="mail-row-copy">
                  <span className="mail-row-line"><strong>{message.sender_name || message.sender}</strong><time>{formatDate(message.received_at)}</time></span>
                  <span className="mail-row-subject">{message.subject || '(No subject)'}</span>
                  <span className="mail-row-preview">{message.preview || 'No text preview available'}</span>
                  <span className="mail-recipient">To {message.recipient}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="mail-reader" aria-label="Selected message">
          {detailLoading && <div className="mail-empty">Opening message...</div>}
          {!detailLoading && !selectedMessage && (
            <div className="mail-reader-empty"><span aria-hidden="true">✉</span><strong>Select a message</strong><p>Choose an email from the inbox to read it here.</p></div>
          )}
          {!detailLoading && selectedMessage && (
            <article>
              <div className="mail-reader-actions">
                <button className="mail-button mail-back-button" onClick={() => setSelectedId(null)}>Back to inbox</button>
                <a className="mail-button primary" href={`mailto:${selectedMessage.reply_to || selectedMessage.sender}?subject=${encodeURIComponent(`Re: ${selectedMessage.subject || ''}`)}`}>Reply</a>
                <button className="mail-button" onClick={() => updateStatus(selectedMessage.id, selectedMessage.status === 'archived' ? 'read' : 'archived')}>
                  {selectedMessage.status === 'archived' ? 'Move to inbox' : 'Archive'}
                </button>
              </div>
              <p className="mail-eyebrow">To {selectedMessage.recipient}</p>
              <h2>{selectedMessage.subject || '(No subject)'}</h2>
              <div className="mail-sender-card">
                <span className="mail-avatar" aria-hidden="true">{initials(selectedMessage.sender_name, selectedMessage.sender)}</span>
                <div><strong>{selectedMessage.sender_name || selectedMessage.sender}</strong><a href={`mailto:${selectedMessage.sender}`}>{selectedMessage.sender}</a></div>
                <time>{formatDate(selectedMessage.received_at)}</time>
              </div>
              <div className="mail-body">{selectedMessage.text_body || 'This message does not contain a plain-text body.'}</div>
              {selectedMessage.attachments?.length > 0 && (
                <div className="mail-attachments">
                  <h3>Attachments</h3>
                  {selectedMessage.attachments.map((attachment, index) => (
                    attachment.stored ? (
                      <a key={`${attachment.filename}-${index}`} href={`${API_ROOT}/${selectedMessage.id}/attachments/${index}`}>
                        <span>{attachment.filename || `Attachment ${index + 1}`}</span>
                        <small>{attachment.mimeType || 'File'}</small>
                      </a>
                    ) : (
                      <span className="mail-attachment-unavailable" key={`${attachment.filename}-${index}`}>
                        <span>{attachment.filename || `Attachment ${index + 1}`}</span>
                        <small>Not retained</small>
                      </span>
                    )
                  ))}
                </div>
              )}
            </article>
          )}
        </section>
      </div>
    </main>
  );
};

export default AdminInbox;
